import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { usePermissions } from "@/context/permissions";

import { useFirstAllowedRoute } from "./route-helpers";

interface IPermissionWrapperProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  redirectTo?: string;
  children: React.ReactNode;
}

/**
 * Componente wrapper que verifica permissões antes de renderizar uma rota.
 *
 * Comportamento de redirect quando o usuário não tem acesso:
 *  1. Se `redirectTo` foi informado, usa ele.
 *  2. Senão, redireciona para a primeira rota permitida do usuário
 *     (ver `useFirstAllowedRoute`).
 *  3. Só envia para `/no-permissions` quando o usuário realmente não
 *     tem nenhuma rota permitida.
 */
export function PermissionWrapper({
  permission,
  permissions,
  requireAll = false,
  redirectTo,
  children,
}: IPermissionWrapperProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } =
    usePermissions();
  const firstAllowedRoute = useFirstAllowedRoute();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  } else {
    hasAccess = true;
  }

  if (!hasAccess) {
    const destination = redirectTo ?? firstAllowedRoute ?? "/no-permissions";

    if (destination === location.pathname) {
      return null;
    }

    return (
      <Navigate
        to={destination}
        state={{ from: location.pathname || "/" }}
        replace
      />
    );
  }

  return <>{children}</>;
}
